import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Diamond({ runners }) {
  const baseClass = "w-6 h-6 border-2 rotate-45 transition-colors";
  return (
    <div className="flex flex-col items-center gap-0.5" data-testid="diamond">
      <div className={`${baseClass} ${runners?.second ? "bg-astros-orange border-astros-orange" : "border-astros-border bg-transparent"}`}></div>
      <div className="flex gap-6">
        <div className={`${baseClass} ${runners?.third ? "bg-astros-orange border-astros-orange" : "border-astros-border bg-transparent"}`}></div>
        <div className={`${baseClass} ${runners?.first ? "bg-astros-orange border-astros-orange" : "border-astros-border bg-transparent"}`}></div>
      </div>
    </div>
  );
}

function CountDisplay({ count }) {
  if (!count) return null;
  return (
    <div className="flex items-center gap-4 text-sm" data-testid="count-display">
      <div className="flex items-center gap-1">
        <span className="text-astros-muted text-xs w-4">B</span>
        {[0, 1, 2, 3].map(i => (
          <div key={i} className={`w-3 h-3 rounded-full ${i < count.balls ? "bg-green-500" : "bg-astros-border"}`}></div>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-astros-muted text-xs w-4">S</span>
        {[0, 1, 2].map(i => (
          <div key={i} className={`w-3 h-3 rounded-full ${i < count.strikes ? "bg-astros-orange" : "bg-astros-border"}`}></div>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-astros-muted text-xs w-4">O</span>
        {[0, 1, 2].map(i => (
          <div key={i} className={`w-3 h-3 rounded-full ${i < count.outs ? "bg-red-500" : "bg-astros-border"}`}></div>
        ))}
      </div>
    </div>
  );
}

export default function LiveGameTracker() {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noGame, setNoGame] = useState(false);
  const intervalRef = useRef(null);

  const fetchGame = async () => {
    try {
      const res = await axios.get(`${API}/games/live/detail`);
      if (res.data.game) {
        setGame(res.data.game);
        setNoGame(false);
      } else {
        setNoGame(true);
        setGame(null);
      }
    } catch (e) {
      console.error("Error fetching live game:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGame();
    intervalRef.current = setInterval(fetchGame, 10000); // 10s updates
    return () => clearInterval(intervalRef.current);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (noGame || !game) {
    return (
      <div className="bg-astros-surface border border-astros-border p-6" data-testid="live-game-tracker">
        <h2 className="font-bebas text-2xl text-white mb-2">LIVE GAME TRACKER</h2>
        <p className="text-astros-muted">No game in progress. Check back on game day!</p>
      </div>
    );
  }

  const isLive = game.status === "In Progress" || game.status === "Live";
  const isScheduled = game.status === "Scheduled" || game.status === "Pre-Game" || game.status === "Warmup";

  return (
    <div className="bg-astros-surface border border-astros-border" data-testid="live-game-tracker">
      {/* Header */}
      <div className="p-4 border-b border-astros-border bg-astros-navy/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isLive && (
              <span className="flex items-center gap-1.5 bg-red-600/20 text-red-400 px-2 py-0.5 text-xs font-bold uppercase">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                LIVE
              </span>
            )}
            <h2 className="font-bebas text-2xl text-white">
              {isScheduled ? "UPCOMING GAME" : "LIVE GAME TRACKER"}
            </h2>
          </div>
          {isLive && (
            <span className="text-astros-muted text-xs">
              {game.inning_state} {game.inning}
            </span>
          )}
        </div>
      </div>

      {/* Scoreboard */}
      <div className="p-4 border-b border-astros-border">
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <p className={`font-bebas text-xl ${game.away_team?.includes("Astros") ? "text-astros-orange" : "text-white"}`}>
              {game.away_team}
            </p>
            <p className="font-bebas text-4xl text-white">{game.away_score}</p>
          </div>
          <div className="text-astros-muted font-bebas text-xl px-4">VS</div>
          <div className="text-center flex-1">
            <p className={`font-bebas text-xl ${game.home_team?.includes("Astros") ? "text-astros-orange" : "text-white"}`}>
              {game.home_team}
            </p>
            <p className="font-bebas text-4xl text-white">{game.home_score}</p>
          </div>
        </div>
      </div>

      {/* Line Score */}
      {game.innings?.length > 0 && (
        <div className="p-3 border-b border-astros-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-astros-muted">
                <th className="text-left pr-3 font-medium py-1">Team</th>
                {game.innings.map(inn => (
                  <th key={inn.num} className="text-center px-1.5 py-1 font-medium min-w-[24px]">{inn.num}</th>
                ))}
                <th className="text-center px-2 py-1 font-bold border-l border-astros-border">R</th>
              </tr>
            </thead>
            <tbody>
              <tr className={game.away_team?.includes("Astros") ? "text-astros-orange font-semibold" : "text-white"}>
                <td className="pr-3 py-1 text-xs">{game.away_team?.split(" ").pop()}</td>
                {game.innings.map(inn => (
                  <td key={inn.num} className="text-center px-1.5 py-1">{inn.away ?? "-"}</td>
                ))}
                <td className="text-center px-2 py-1 font-bold border-l border-astros-border">{game.away_score}</td>
              </tr>
              <tr className={game.home_team?.includes("Astros") ? "text-astros-orange font-semibold" : "text-white"}>
                <td className="pr-3 py-1 text-xs">{game.home_team?.split(" ").pop()}</td>
                {game.innings.map(inn => (
                  <td key={inn.num} className="text-center px-1.5 py-1">{inn.home ?? "-"}</td>
                ))}
                <td className="text-center px-2 py-1 font-bold border-l border-astros-border">{game.home_score}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Current At-Bat: Matchup + Count + Diamond */}
      {isLive && (
        <div className="p-4 border-b border-astros-border">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              {game.matchup?.batter && (
                <div className="mb-3">
                  <p className="text-xs text-astros-muted uppercase tracking-wider">At Bat</p>
                  <p className="text-white font-semibold">{game.matchup.batter}</p>
                </div>
              )}
              {game.matchup?.pitcher && (
                <div className="mb-3">
                  <p className="text-xs text-astros-muted uppercase tracking-wider">Pitching</p>
                  <p className="text-white font-semibold">{game.matchup.pitcher}</p>
                </div>
              )}
              <CountDisplay count={game.count} />
            </div>
            <Diamond runners={game.runners} />
          </div>
        </div>
      )}

      {/* Recent Plays */}
      {game.recent_plays?.length > 0 && (
        <div className="p-4 border-b border-astros-border">
          <h3 className="text-xs text-astros-muted uppercase tracking-wider mb-2 font-bold">Recent Plays</h3>
          <div className="space-y-2">
            {game.recent_plays.slice().reverse().map((play, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="text-sm text-white/80 border-l-2 border-astros-border pl-3 py-1"
              >
                {play.event && (
                  <span className={`text-xs font-bold mr-2 ${play.rbi > 0 ? "text-astros-orange" : "text-astros-muted"}`}>
                    {play.event}
                  </span>
                )}
                <span className="text-xs">{play.description}</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Scoring Plays */}
      {game.scoring_plays?.length > 0 && (
        <div className="p-4">
          <h3 className="text-xs text-astros-muted uppercase tracking-wider mb-2 font-bold">Scoring Plays</h3>
          <div className="space-y-2">
            {game.scoring_plays.slice().reverse().map((play, i) => (
              <div key={i} className="text-xs border-l-2 border-astros-orange pl-3 py-1">
                <span className="text-astros-orange font-bold mr-2">{play.inning}</span>
                <span className="text-white/80">{play.description}</span>
                <span className="text-astros-muted ml-2">({play.away_score}-{play.home_score})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
